import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Box, makeStyles } from "@material-ui/core";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/auth/AuthContext";
import PullToRefresh from "react-simple-pull-to-refresh";
import {
  getCommandListPageData,
  getCommandListPageDataSuccess,
} from "../../store/command-list-page.store";
import {
  addCalendarEventData,
  addCalendarEventDataSuccess,
  addCalendarEventResetState,
} from "../../store/add-calendar-event-page.store";
import Error from "../../components/Error";
import Loading from "../../components/Loading";
import AddCalendarEventForm from "../../forms/calendar-event/AddCalendarEventForm";
import LoadingBackdrop from "../../components/LoadingBackdrop";
import Success from "../../components/Success";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "5px",
    alignItems: "center",
  },
  newEventButton: {
    width: "150px",
    height: "35px",
    border: "solid black 1px",
    color: "grey",
    backgroundColor: "white",
  },
}));

const AddCalendarEventContainer = ({ eklok }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const history = useHistory();

  const { token } = useContext(AuthContext);

  const { dataFetched, error, loading } = useSelector((state) => {
    return state.commandListPage;
  });

  const { eklokData, commandList } = useSelector((state) => {
    return state.manualCommandList;
  });

  const {
    addCalendarEventData: dataAdded,
    loading: creationLoading,
    addCalendarEventDataSuccess: addSuccess,
  } = useSelector((state) => {
    return state.addCalendarEvent;
  });

  const [eventActions, setEventActions] = useState([]);

  const handleEventActionsChange = (selectedOptions) => {
    const currentSelectedOptions = selectedOptions
      ? selectedOptions?.map((option) => option.value)
      : [];
    setEventActions(currentSelectedOptions);
    return currentSelectedOptions;
  };

  const initialValues = {
    EVENT_NAME: "",
    EVENT_DESCRIPTION: "",
    EVENT_START_DATE: undefined,
    EVENT_ACTION_LIST: eventActions,
  };

  const handleAddEvent = (values) => {
    const {
      EVENT_NAME,
      EVENT_DESCRIPTION,
      EVENT_START_DATE,
      EVENT_ACTION_LIST,
    } = values;

    if (
      EVENT_NAME &&
      EVENT_DESCRIPTION &&
      EVENT_START_DATE &&
      EVENT_ACTION_LIST.length > 0
    ) {
      const payloadBody = {
        EVENT_NAME,
        EVENT_DESCRIPTION,
        EVENT_START_DATE,
        EVENT_ACTION_LIST: eventActions,
      };

      dispatch(
        addCalendarEventData({
          token: token,
          eklok: eklok,
          body: { eklok: eklok, mqtt_payload: payloadBody },
        }),
      );
    } else {
      dispatch(addCalendarEventDataSuccess(dataAdded));
    }
  };

  const audioChannel = ["MELODIES", "MUSICS_SYNTH"];
  const bellChannel = ["MUSICS"];

  const isRoutine = (action) => action.ACTION_TYPE === "ACTIONS";
  const isAudio = (action) => audioChannel.includes(action.ACTION_TYPE);
  const isBell = (action) => bellChannel.includes(action.ACTION_TYPE);

  const isActionDisabled = (action) => {
    if (isRoutine(action)) return false;

    const hasAudioSelected = initialValues.EVENT_ACTION_LIST.some(
      (selectedId) =>
        isAudio(commandList.all.find((cmd) => cmd.ACTION_ID === selectedId)),
    );

    const hasBellSelected = initialValues.EVENT_ACTION_LIST.some((selectedId) =>
      isBell(commandList.all.find((cmd) => cmd.ACTION_ID === selectedId)),
    );

    if (isAudio(action) && hasAudioSelected) return true;
    if (isBell(action) && hasBellSelected) return true;

    return false;
  };

  const getGroupedOptions = () => {
    return Object.entries({
      MUSICS: commandList.musics,
      MELODIES: commandList.melodies,
      MUSICS_SYNTH: commandList.musicsSynth,
      ACTIONS: commandList.routines,
    }).map(([group, actions]) => ({
      label: t(`action_type.${group}`),
      options: actions.map((action) => ({
        value: action.ACTION_ID,
        label: action.ACTION_NAME,
        isDisabled: isActionDisabled(action),
      })),
    }));
  };

  const onRefresh = () =>
    new Promise((resolve, reject) => {
      dispatch(
        getCommandListPageData({
          token: token,
          eklok: eklok,
        }),
      );

      resolve();
    });

  const onAddedSuccess = () => {
    setEventActions([]);
    dispatch(addCalendarEventResetState());
    history.push("/app/calendar/event/" + eklok);
  };

  useEffect(() => {
    if (!eklokData || eklokData.id !== eklok || commandList.all === null) {
      dispatch(
        getCommandListPageData({
          token: token,
          eklok: eklok,
        }),
      );
    } else {
      dispatch(getCommandListPageDataSuccess());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PullToRefresh onRefresh={onRefresh} pullingContent={false}>
      {loading && !creationLoading && <Loading />}
      {creationLoading && !loading && <LoadingBackdrop />}
      <Box className={classes.container}>
        {error && <Error message={error.message} />}
        {addSuccess && <Success onClose={onAddedSuccess} />}
        {dataFetched && (
          <AddCalendarEventForm
            commandList={getGroupedOptions()}
            initialValues={initialValues}
            isLoading={creationLoading}
            onSetEventActions={handleEventActionsChange}
            onSubmit={handleAddEvent}
          />
        )}
      </Box>
    </PullToRefresh>
  );
};
export default AddCalendarEventContainer;
